import React from "react";
import { Box, Grid, Stack, Typography, LinearProgress } from "@mui/material";
import { leavePalette, cardSx, typeMeta } from "./leaveHelpers";

// "Leave Balance (<year>)" row — one card per leave type from leavetypefilter.
// Entitled = credited_leave, Used = used_leave, Available = credited - used.
// Pending is derived from the leave requests (business_days of this type still
// Pending) since leavetypefilter doesn't carry a pending figure of its own.
export const LeaveBalanceCards = ({ leaveTypes, pendingByType, yearLabel }) => {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.5 }}
      >
        <Typography fontWeight={700} sx={{ color: leavePalette.text, fontSize: 17 }}>
          Leave Balance{yearLabel ? ` (${yearLabel})` : ""}
        </Typography>
      </Stack>
      <Grid container spacing={2}>
        {leaveTypes.length === 0 ? (
          <Grid item xs={12}>
            <Box sx={{ ...cardSx, p: 2, color: leavePalette.subText }}>
              No leave types configured.
            </Box>
          </Grid>
        ) : (
          leaveTypes.map((lt, idx) => {
            const meta = typeMeta(lt.leave_type);
            const Icon = meta.icon;
            const entitled = Number(lt.credited_leave) || 0;
            const used = Number(lt.used_leave) || 0;
            const pending = Number(pendingByType?.[lt.leave_type] || 0);
            const available = entitled - used;
            const usedPct = entitled ? Math.min((used / entitled) * 100, 100) : 0;
            return (
              <Grid item xs={12} sm={6} md={3} key={lt.leave_type || idx}>
                <Box sx={{ ...cardSx, p: 2, height: "100%" }}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: `${meta.color}1A`,
                        color: meta.color,
                      }}
                    >
                      <Icon fontSize="small" />
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ color: leavePalette.subText }}>
                        {lt.leave_type}
                      </Typography>
                      <Typography variant="h6" fontWeight={700} sx={{ color: leavePalette.text }}>
                        {available}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: leavePalette.subText }}>
                      Entitled
                    </Typography>
                    <Typography variant="caption" fontWeight={600}>
                      {entitled}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: leavePalette.subText }}>
                      Used
                    </Typography>
                    <Typography variant="caption" fontWeight={600}>
                      {used}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ color: leavePalette.subText }}>
                      Pending
                    </Typography>
                    <Typography variant="caption" fontWeight={600}>
                      {pending}
                    </Typography>
                  </Stack>

                  <LinearProgress
                    variant="determinate"
                    value={usedPct}
                    sx={{
                      height: 6,
                      borderRadius: 5,
                      bgcolor: "#EEF0F3",
                      "& .MuiLinearProgress-bar": { bgcolor: meta.color },
                    }}
                  />
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{ color: meta.color, mt: 1, display: "block" }}
                  >
                    Available: {available}
                  </Typography>
                </Box>
              </Grid>
            );
          })
        )}
      </Grid>
    </Box>
  );
};
